import { useEffect, useRef, useState, type ChangeEvent } from "react";
import { ProblemError, useApi, usePrincipal, type Scope } from "@csa/web-foundation";
import { useT } from "../../i18n/index";
import type { MessageKey } from "../../i18n/dictionaries";
import { Icon } from "../../ui/icons";
import {
  useDeleteMedia,
  usePreferences,
  useStaffMedia,
  useUpdatePreferences,
  useUploadMedia,
  type AccessibilityFontScale,
  type StaffDefaultScope,
  type StaffLocale,
  type StaffMediaKind,
  type StaffMediaMeta,
  type StaffPreferences,
  type UpdateStaffPreferences,
} from "./use-preferences";

// HESAP & AYARLAR — the per-staff PERSONAL settings surface (secondary-surfaces §3),
// replacing the HESAP-cluster placeholder (#201) with a real read/write over the
// READY backend (#237: GET/PUT /v1/me/preferences). Drawn on OUR design system
// (`surface`/`k-*` kit + existing tokens) — NO new palette, NO restyled buttons.
// Sectioned + calm: Profil (name/role, READ-ONLY — identity is governed, not
// self-editable here), Tercihler (locale wired to the app i18n + persisted, plus a
// bounded notification threshold + accessibility toggles), Kapsam (a manager's
// default landing scope — hidden for a plain own-scope advisor), Güvenlik (MFA/
// passkey/session live in the Keycloak account console — a LINK, never a faked
// form), and a Foto/imza "yakında" note (object-storage follow-on). This is PERSONAL
// settings, NOT business config — no config / Score Registry here.

const SCOPE_RANK: Record<Scope, number> = { own: 1, team: 2, all: 3 };

// The Keycloak account console (MFA/passkey/sessions/devices) — an explicit env,
// else derived from the staff realm issuer, else null → a calm note (no dead link).
function accountConsoleUrl(): string | null {
  const explicit = import.meta.env.VITE_ACCOUNT_CONSOLE_URL;
  if (explicit) return explicit;
  const authority = import.meta.env.VITE_OIDC_AUTHORITY;
  if (authority) return `${authority.replace(/\/$/, "")}/account`;
  return null;
}

// The editable slice of the preferences (updatedAt is server-owned, not edited).
type EditablePrefs = Pick<
  StaffPreferences,
  "locale" | "defaultScope" | "notificationPref" | "accessibility"
>;

function toEditable(p: StaffPreferences): EditablePrefs {
  return {
    locale: p.locale,
    defaultScope: p.defaultScope,
    notificationPref: { ...p.notificationPref },
    accessibility: { ...p.accessibility },
  };
}

// The dirty diff → the PUT body. Only changed fields are sent (the write is a
// partial merge); the nested jsonb objects are compared + sent wholesale.
function diff(base: StaffPreferences, form: EditablePrefs): UpdateStaffPreferences {
  const out: UpdateStaffPreferences = {};
  if (form.locale !== base.locale) out.locale = form.locale;
  if (form.defaultScope !== base.defaultScope) out.defaultScope = form.defaultScope;
  if (JSON.stringify(form.notificationPref) !== JSON.stringify(base.notificationPref)) {
    out.notificationPref = form.notificationPref;
  }
  if (JSON.stringify(form.accessibility) !== JSON.stringify(base.accessibility)) {
    out.accessibility = form.accessibility;
  }
  return out;
}

export function AccountSettings() {
  const { t, locale, setLocale } = useT();
  const { me } = usePrincipal();
  const { data, isLoading, error, refetch, isFetching } = usePreferences();
  const update = useUpdatePreferences();

  const [form, setForm] = useState<EditablePrefs | null>(null);
  const localeHydrated = useRef(false);

  // Seed the editable form once the preferences land.
  useEffect(() => {
    if (data && form === null) setForm(toEditable(data));
  }, [data, form]);

  // Round-trip: the persisted locale drives the app i18n on first load, so the
  // setting the staffer saved here re-applies across the whole console next visit.
  useEffect(() => {
    if (data && !localeHydrated.current) {
      localeHydrated.current = true;
      if (data.locale !== locale) setLocale(data.locale);
    }
  }, [data, locale, setLocale]);

  // Role/scope affordances are computed from the principal's grants (the API stays
  // the boundary). Kapsam is a manager-only setting — a plain own-scope advisor has
  // no team scope to land on, so the whole section is hidden for them.
  const maxScope = me?.permissions.reduce((m, g) => Math.max(m, SCOPE_RANK[g.scope]), 0) ?? 0;
  const isManager = maxScope >= SCOPE_RANK.team;
  const roleKey: MessageKey =
    maxScope >= SCOPE_RANK.all
      ? "settings.role.admin"
      : maxScope >= SCOPE_RANK.team
        ? "settings.role.manager"
        : "settings.role.advisor";

  const dirtyBody = data && form ? diff(data, form) : {};
  const isDirty = Object.keys(dirtyBody).length > 0;
  const acctUrl = accountConsoleUrl();

  // Change a nested/flat field of the form (typed per-key by the callers below).
  function patch(next: Partial<EditablePrefs>) {
    setForm((f) => (f ? { ...f, ...next } : f));
  }

  // Locale is applied to the app i18n LIVE (the whole console re-renders in the new
  // language immediately); it is persisted to preferences on Save (the round-trip).
  function onLocale(next: StaffLocale) {
    patch({ locale: next });
    setLocale(next);
  }

  function onSave() {
    if (!isDirty || update.isPending) return;
    update.mutate(dirtyBody);
  }

  return (
    <section className="surface" data-testid="account-settings">
      <header className="surface__head">
        <div>
          <h1 className="surface__title">{t("settings.heading")}</h1>
          <p className="surface__subtitle">{t("settings.subtitle")}</p>
        </div>
      </header>

      {isLoading && (
        <div className="k-loading" data-testid="settings-loading">
          <div className="k-loading__head">
            <span className="k-loading__spinner" />
            <span className="k-loading__label">{t("settings.loading")}</span>
          </div>
        </div>
      )}

      {error && (
        <div className="k-note k-note--error" data-testid="settings-load-error">
          <div className="k-note__row">
            <span className="k-note__icon k-note__icon--error">
              <Icon name="alert-triangle" size={20} />
            </span>
            <div>
              <p className="k-note__title k-note__title--sm">{t("settings.error.title")}</p>
              <p role="alert" className="k-note__desc">
                {error instanceof ProblemError ? error.problem.detail : t("settings.error.detail")}
              </p>
              <div className="k-note__actions">
                <button
                  type="button"
                  className="k-btn k-btn--soft k-btn--sm"
                  disabled={isFetching}
                  onClick={() => void refetch()}
                  data-testid="settings-retry"
                >
                  {t("settings.retry")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {data && form && (
        <>
          {/* 1 · PROFİL — name + role, READ-ONLY (identity is governed, not self-edited). */}
          <div className="k-card k-card--pad-lg" data-testid="settings-profile">
            <p className="surface__section-label">{t("settings.profile.title")}</p>
            <div className="settings-readonly">
              <div className="settings-readonly__row">
                <span className="settings-readonly__label">{t("settings.profile.name")}</span>
                <span className="settings-readonly__value" data-testid="settings-name">
                  {me?.displayName ?? me?.sub ?? "—"}
                </span>
              </div>
              <div className="settings-readonly__row">
                <span className="settings-readonly__label">{t("settings.profile.role")}</span>
                <span className="settings-readonly__value" data-testid="settings-role">
                  {t(roleKey)}
                </span>
              </div>
              {me?.email && (
                <div className="settings-readonly__row">
                  <span className="settings-readonly__label">{t("settings.profile.email")}</span>
                  <span className="settings-readonly__value" data-testid="settings-email">
                    {me.email}
                  </span>
                </div>
              )}
            </div>
            <p className="k-field__hint">{t("settings.profile.governed")}</p>
          </div>

          {/* 2 · TERCİHLER — locale (app i18n + persisted), notification floor, a11y. */}
          <div className="k-card k-card--pad-lg" data-testid="settings-prefs">
            <p className="surface__section-label">{t("settings.prefs.title")}</p>

            <div className="k-field">
              <span className="k-field__label">{t("settings.prefs.locale")}</span>
              <div
                className="k-langtoggle"
                role="group"
                aria-label={t("settings.prefs.locale")}
                data-testid="settings-locale"
              >
                {(["tr", "en"] as const).map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    className={`k-langtoggle__opt${form.locale === opt ? " is-active" : ""}`}
                    onClick={() => onLocale(opt)}
                    aria-pressed={form.locale === opt}
                    data-testid={`settings-locale-${opt}`}
                  >
                    {opt.toUpperCase()}
                  </button>
                ))}
              </div>
              <span className="k-field__hint">{t("settings.prefs.locale.hint")}</span>
            </div>

            <div className="k-field">
              <span className="k-field__label">{t("settings.prefs.notifications")}</span>
              <label className="k-choice k-choice--switch">
                <input
                  type="checkbox"
                  checked={form.notificationPref.severityThreshold === "critical_only"}
                  onChange={(e) =>
                    patch({
                      notificationPref: {
                        severityThreshold: e.target.checked ? "critical_only" : "all",
                      },
                    })
                  }
                  data-testid="settings-critical-only"
                />
                <span className="k-switch">
                  <span className="k-switch__knob" />
                </span>
                <span>{t("settings.prefs.criticalOnly")}</span>
              </label>
              <span className="k-field__hint">{t("settings.prefs.criticalOnly.hint")}</span>
            </div>

            <div className="k-field">
              <span className="k-field__label">{t("settings.prefs.accessibility")}</span>
              <div className="settings-toggles">
                <label className="k-choice k-choice--switch">
                  <input
                    type="checkbox"
                    checked={form.accessibility.reducedMotion}
                    onChange={(e) =>
                      patch({
                        accessibility: { ...form.accessibility, reducedMotion: e.target.checked },
                      })
                    }
                    data-testid="settings-reduced-motion"
                  />
                  <span className="k-switch">
                    <span className="k-switch__knob" />
                  </span>
                  <span>{t("settings.a11y.reducedMotion")}</span>
                </label>
                <label className="k-choice k-choice--switch">
                  <input
                    type="checkbox"
                    checked={form.accessibility.highContrast}
                    onChange={(e) =>
                      patch({
                        accessibility: { ...form.accessibility, highContrast: e.target.checked },
                      })
                    }
                    data-testid="settings-high-contrast"
                  />
                  <span className="k-switch">
                    <span className="k-switch__knob" />
                  </span>
                  <span>{t("settings.a11y.highContrast")}</span>
                </label>
              </div>
              <div className="settings-inline-field">
                <span className="k-field__label">{t("settings.a11y.fontScale")}</span>
                <div className="k-select settings-select">
                  <select
                    className="k-select__native"
                    value={form.accessibility.fontScale}
                    onChange={(e) =>
                      patch({
                        accessibility: {
                          ...form.accessibility,
                          fontScale: e.target.value as AccessibilityFontScale,
                        },
                      })
                    }
                    data-testid="settings-font-scale"
                  >
                    <option value="normal">{t("settings.a11y.fontScale.normal")}</option>
                    <option value="large">{t("settings.a11y.fontScale.large")}</option>
                  </select>
                  <span className="k-select__chevron" aria-hidden="true">
                    <Icon name="chevron-down" size={16} />
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* 3 · KAPSAM — a manager's default landing scope (own/team). Hidden for a
              plain advisor (no team scope to land on). */}
          {isManager && (
            <div className="k-card k-card--pad-lg" data-testid="settings-scope">
              <p className="surface__section-label">{t("settings.scope.title")}</p>
              <div className="k-field">
                <span className="k-field__label">{t("settings.scope.default")}</span>
                <div
                  className="k-langtoggle"
                  role="group"
                  aria-label={t("settings.scope.default")}
                  data-testid="settings-scope-toggle"
                >
                  {(["own", "team"] as const).map((opt: StaffDefaultScope) => (
                    <button
                      key={opt}
                      type="button"
                      className={`k-langtoggle__opt${form.defaultScope === opt ? " is-active" : ""}`}
                      onClick={() => patch({ defaultScope: opt })}
                      aria-pressed={form.defaultScope === opt}
                      data-testid={`settings-scope-${opt}`}
                    >
                      {t(opt === "own" ? "settings.scope.own" : "settings.scope.team")}
                    </button>
                  ))}
                </div>
                <span className="k-field__hint">{t("settings.scope.hint")}</span>
              </div>
            </div>
          )}

          {/* Save bar — a single explicit Save persisting the dirty diff (PUT). */}
          <div className="surface__actions" data-testid="settings-savebar">
            {update.isSuccess && !isDirty && (
              <span className="k-note__title k-note__title--sm" data-testid="settings-saved">
                {t("settings.saved")}
              </span>
            )}
            {update.isError && (
              <span role="alert" className="k-field__error" data-testid="settings-save-error">
                {update.error instanceof ProblemError
                  ? update.error.problem.detail
                  : t("settings.error.detail")}
              </span>
            )}
            <button
              type="button"
              className="k-btn k-btn--primary"
              disabled={!isDirty || update.isPending}
              onClick={onSave}
              data-testid="settings-save"
            >
              {update.isPending ? t("settings.saving") : t("settings.save")}
            </button>
          </div>

          {/* 4 · GÜVENLİK — MFA/passkey/session/device live in the Keycloak account
              console, NOT this API. A calm LINK (or a note when no URL), never a form. */}
          <div className="k-card k-card--pad-lg" data-testid="settings-security">
            <p className="surface__section-label">{t("settings.security.title")}</p>
            <p className="k-field__hint">{t("settings.security.desc")}</p>
            {acctUrl ? (
              <a
                className="k-btn k-btn--soft"
                href={acctUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="settings-security-link"
              >
                <Icon name="lock" size={16} />
                <span>{t("settings.security.open")}</span>
                <Icon name="arrow-right" size={15} />
              </a>
            ) : (
              <p className="k-field__hint" data-testid="settings-security-note">
                {t("settings.security.note")}
              </p>
            )}
          </div>

          {/* 5 · FOTO / İMZA — the caller's PERSONAL media (#244). Encrypted at rest;
              self-scoped upload / preview / remove. NOT business config. */}
          <div className="k-card k-card--pad-lg" data-testid="settings-media">
            <p className="surface__section-label">{t("settings.media.title")}</p>
            <p className="k-field__hint">{t("settings.media.desc")}</p>
            <div className="settings-media__slots">
              <MediaSlot kind="photo" labelKey="settings.media.photo" />
              <MediaSlot kind="signature" labelKey="settings.media.signature" />
            </div>
          </div>
        </>
      )}
    </section>
  );
}

// One media kind (photo / signature): a live preview of the stored object (fetched
// as an owner-decrypt blob → object URL), plus Yükle (replace) and Kaldır. The
// upload validates client-side hints (image + size) but the API re-checks the real
// bytes. Object URLs are revoked on change/unmount (no leak).
function MediaSlot({ kind, labelKey }: { kind: StaffMediaKind; labelKey: MessageKey }) {
  const { t } = useT();
  const api = useApi();
  const media = useStaffMedia();
  const upload = useUploadMedia();
  const remove = useDeleteMedia();
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const apiKind = kind === "photo" ? "PHOTO" : "SIGNATURE";
  const meta: StaffMediaMeta | undefined = (media.data ?? []).find((m) => m.kind === apiKind);
  const stamp = meta?.updatedAt ?? null;

  // Fetch the stored bytes as an object URL whenever the object changes (by as-of).
  useEffect(() => {
    let url: string | null = null;
    let cancelled = false;
    if (!stamp) {
      setPreviewUrl(null);
      return;
    }
    void api
      .getBlob(`/v1/me/media/${kind}`)
      .then((blob) => {
        if (cancelled) return;
        url = URL.createObjectURL(blob);
        setPreviewUrl(url);
      })
      .catch(() => {
        if (!cancelled) setPreviewUrl(null);
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [api, kind, stamp]);

  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    setError(null);
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    upload.mutate(
      { kind, file },
      {
        onError: (err) =>
          setError(err instanceof ProblemError ? err.problem.detail : t("settings.media.error")),
      },
    );
  };

  const busy = upload.isPending || remove.isPending;

  return (
    <div className="settings-media__slot" data-testid={`media-slot-${kind}`}>
      <div className="settings-media__preview" aria-hidden={!previewUrl}>
        {previewUrl ? (
          <img src={previewUrl} alt={t(labelKey)} data-testid={`media-preview-${kind}`} />
        ) : (
          <Icon name={kind === "photo" ? "member" : "edit"} size={22} />
        )}
      </div>
      <div className="settings-media__body">
        <p className="settings-media__label">{t(labelKey)}</p>
        <div className="k-field__actions">
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={onPick}
            style={{ display: "none" }}
            data-testid={`media-input-${kind}`}
          />
          <button
            type="button"
            className="k-btn k-btn--soft k-btn--sm"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
            data-testid={`media-upload-${kind}`}
          >
            {meta ? t("settings.media.replace") : t("settings.media.upload")}
          </button>
          {meta && (
            <button
              type="button"
              className="k-btn k-btn--ghost k-btn--sm"
              disabled={busy}
              onClick={() =>
                remove.mutate(kind, {
                  // remove had NO onError, and the local `error` state was only
                  // ever set by upload — so a failed delete looked like success
                  // until a reload. Route it through the same channel.
                  onError: (err) =>
                    setError(
                      err instanceof ProblemError ? err.problem.detail : t("settings.media.error"),
                    ),
                })
              }
              data-testid={`media-remove-${kind}`}
            >
              {t("settings.media.remove")}
            </button>
          )}
        </div>
        {error && (
          <p role="alert" className="k-field__error" data-testid={`media-error-${kind}`}>
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
